import {
  Building2,
  CalendarDays,
  ShieldCheck,
  UserRound,
  Users,
  Home,
  Activity,
} from "lucide-react";

import type { PlatformChurchDetail } from "@/api/platform";

type ChurchOverviewPanelProps = {
  church: PlatformChurchDetail;
};

export function ChurchOverviewPanel({
  church,
}: ChurchOverviewPanelProps) {
  const overview = church.church;

  return (
    <section>
      <SectionHeading
        title="Account overview"
        description="Platform-level configuration and lifecycle information."
      />

      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <DetailCard
          icon={ShieldCheck}
          label="Activation"
          value={formatValue(overview.activation_status ?? overview.status)}
          description={overview.activation_requirement ?? "The initial administrator completed activation."}
        />

        <DetailCard
          icon={UserRound}
          label="Administrator activation"
          value={overview.administrator_activated ? "Activated" : "Awaiting invitation acceptance"}
        />

        <DetailCard
          icon={Building2}
          label="Location"
          value={[overview.city, overview.state].filter(Boolean).join(", ") || "Not provided"}
        />

        <DetailCard icon={Users} label="Members" value={String(overview.member_count ?? 0)} />
        <DetailCard icon={Home} label="Households" value={String(overview.household_count ?? 0)} />
        <DetailCard icon={Activity} label="Attendance records" value={String(overview.attendance_count ?? 0)} />
        <DetailCard icon={CalendarDays} label="Service schedules" value={String(overview.service_schedule_count ?? 0)} />
        <DetailCard icon={CalendarDays} label="Service occurrences" value={String(overview.service_occurrence_count ?? 0)} />
        <DetailCard icon={Users} label="Visitors" value={String(overview.visitor_count ?? 0)} />
        <DetailCard icon={DatabaseIcon} label="Demo data" value={overview.is_seeded ? "Seeded" : "Not seeded"} />
        <DetailCard
          icon={ShieldCheck}
          label="Lifecycle stage"
          value={formatValue(overview.lifecycle_stage)}
        />

        <DetailCard
          icon={Building2}
          label="Plan"
          value={overview.plan_code ?? "Not assigned"}
        />

        <DetailCard
          icon={UserRound}
          label="User capacity"
          value={String(overview.max_users)}
        />

        <DetailCard
          icon={Building2}
          label="Member record capacity"
          value={String(overview.max_member_records)}
        />

        <DetailCard
          icon={CalendarDays}
          label="Pilot started"
          value={formatDate(overview.pilot_started_at)}
        />

        <DetailCard
          icon={CalendarDays}
          label="Pilot expires"
          value={formatDate(overview.pilot_expires_at)}
        />
      </div>
    </section>
  );
}

type DetailCardProps = {
  icon: React.ElementType;
  label: string;
  value: string;
  description?: string;
};

function DetailCard({
  icon: Icon,
  label,
  value,
  description,
}: DetailCardProps) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
        <Icon className="h-4 w-4 text-slate-600" />
      </div>

      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>

      <p className="mt-1 text-base font-semibold text-slate-950">
        {value}
      </p>
      {description && <p className="mt-2 text-xs leading-5 text-slate-500">{description}</p>}
    </div>
  );
}

function SectionHeading({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-950">
        {title}
      </h3>

      <p className="mt-1 text-sm leading-6 text-slate-500">
        {description}
      </p>
    </div>
  );
}

function formatValue(value?: string | null) {
  if (!value) {
    return "Not available";
  }

  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDate(value?: string | null) {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Not available";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

const DatabaseIcon = Building2;
